import { useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type {
  TypeCurveScenarioOption,
  TypeCurveScenarioOptionsResponse,
} from 'types/type-curve-scenario-options';

/**
 * Query-key prefix for the promoted Type Curve Scenario options read — one cache
 * entry, since the request is org-wide and takes no per-scenario argument.
 */
export const TYPE_CURVE_SCENARIO_OPTIONS_QUERY_KEY =
  'type-curve-scenario-options' as const;

/** The endpoint's slim promoted-scenarios lookup (see 2.1). */
const OPTIONS_PATH = 'type_curve_scenarios';

/** What {@link useTypeCurveScenarioOptions} returns. */
export interface UseTypeCurveScenarioOptions {
  /** Promoted-content scenarios as `{ id, name }`, name-ordered (empty while loading/on error). */
  options: TypeCurveScenarioOption[];
  /** True while the read is in flight. */
  loading: boolean;
  /** The failed read, or null. */
  error: Error | null;
}

/**
 * Loads the promoted Type Curve Scenario options for any scenario dropdown —
 * `GET /type_curve_scenarios?promoted=true`, JSON negotiated via the Accept
 * header (ky's `.json()` sets it; no `.json` suffix on the path).
 *
 * The server owns the population rule: `promoted=true` returns only scenarios
 * with current-promoted content, already ordered by name. An empty list is not
 * an error — the editor then shows the dropdown empty with its required-field
 * message.
 *
 * React Query owns the request: one shared cache entry, deduped across
 * re-renders and cancelled through the injected `signal`.
 *
 * @return {UseTypeCurveScenarioOptions} The options, load state, and any error.
 */
export function useTypeCurveScenarioOptions(): UseTypeCurveScenarioOptions {
  const { data, isFetching, error } = useQuery<
    TypeCurveScenarioOption[],
    Error
  >({
    queryKey: [TYPE_CURVE_SCENARIO_OPTIONS_QUERY_KEY],
    queryFn: async ({ signal }) => {
      const response = await api
        .get(OPTIONS_PATH, {
          searchParams: { promoted: 'true' },
          signal,
        })
        .json<TypeCurveScenarioOptionsResponse>();
      return response.typeCurveScenarios;
    },
    // Promoted content changes only when a type curve is promoted/revoked, so a
    // revisit shouldn't re-hit the server within the standard window.
    staleTime: 300_000,
  });

  return {
    options: data ?? [],
    loading: isFetching,
    error: error ?? null,
  };
}
